import { designDocumentSchema } from "./designs.js";
import { shippingAddressSchema, shippingStatusSchema } from "./checkout.js";
import { millimeterSchema, moneyFenSchema, positiveIntSchema, uuidSchema } from "./primitives.js";
import { z } from "zod";
import { shippingQuoteSchema } from "./shipping.js";

export const packagingOptionSchema = z.enum(["standard", "gift"]);
export type PackagingOption = z.infer<typeof packagingOptionSchema>;

export const certificateOptionSchema = z.enum(["none", "certificate"]);
export type CertificateOption = z.infer<typeof certificateOptionSchema>;

/** 公开报价请求：客户端只能提交设计，价格与库存由服务端重新读取。 */
export const quoteRequestSchema = z.object({
  design: designDocumentSchema,
  packaging: packagingOptionSchema.default("standard"),
  certificate: certificateOptionSchema.default("none"),
  address: shippingAddressSchema.nullable().default(null),
});

export type QuoteRequestPayload = z.infer<typeof quoteRequestSchema>;

/** 购物车勾选多份设计时，按整单汇总材料和运费。 */
export const batchQuoteRequestSchema = quoteRequestSchema.omit({ design: true }).extend({
  designs: z.array(designDocumentSchema).min(1).max(20),
});
export type BatchQuoteRequestPayload = z.infer<typeof batchQuoteRequestSchema>;

/** 一档材料在当前目录下的报价与可售状态。 */
export const quoteLineSchema = z.object({
  skuId: uuidSchema,
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  diameterMm: millimeterSchema.positive(),
  quantity: positiveIntSchema,
  unitPriceFen: moneyFenSchema.nonnegative(),
  subtotalFen: moneyFenSchema.nonnegative(),
  available: z.boolean(),
  availabilityCode: z.enum(["AVAILABLE", "OUT_OF_STOCK", "SPEC_VERSION_CHANGED"]),
});

export type QuoteLinePayload = z.infer<typeof quoteLineSchema>;

export const quoteOptionSchema = z.object({
  key: z.string().min(1).max(32),
  label: z.string().min(1).max(32),
  description: z.string().min(1).max(128),
  priceFen: moneyFenSchema.nonnegative(),
});

/**
 * 报价覆盖材料与结算预览中的可选服务。
 *
 * totalFen 为已知金额（包含已确定的运费）；payableFen 仅在材料与运费均确定时返回。
 * 报价不代表已创建订单或已支付，订单仍须冻结此时快照。
 */
export const quoteResponseSchema = z
  .object({
    catalogRevision: positiveIntSchema,
    itemCount: z.int().nonnegative(),
    materialFen: moneyFenSchema.nonnegative(),
    packaging: packagingOptionSchema,
    certificate: certificateOptionSchema,
    options: z.object({
      packaging: z.array(quoteOptionSchema),
      certificate: z.array(quoteOptionSchema),
    }),
    packagingFen: moneyFenSchema.nonnegative(),
    certificateFen: moneyFenSchema.nonnegative(),
    shippingFen: moneyFenSchema.nonnegative().nullable(),
    shippingStatus: shippingStatusSchema,
    shipping: shippingQuoteSchema,
    payableFen: moneyFenSchema.nonnegative().nullable(),
    lines: z.array(quoteLineSchema),
    unknownSkuIds: z.array(uuidSchema),
    blockers: z.array(z.enum(["EMPTY_DESIGN", "SKU_UNAVAILABLE", "OUT_OF_STOCK"])),
    pricingReady: z.boolean(),
    totalFen: moneyFenSchema.nonnegative().nullable(),
  })
  .superRefine((value, ctx) => {
    const expectedTotal = value.pricingReady
      ? value.materialFen + value.packagingFen + value.certificateFen + (value.shippingFen ?? 0)
      : null;
    if (
      value.shippingStatus !== value.shipping.status ||
      value.shippingFen !== value.shipping.feeFen ||
      (value.shippingStatus === "READY") !== (value.shippingFen !== null) ||
      value.totalFen !== expectedTotal ||
      value.payableFen !== (value.shippingStatus === "READY" ? expectedTotal : null)
    ) {
      ctx.addIssue({ code: "custom", message: "运费状态与应付金额不一致" });
    }
  });

export type QuoteResponsePayload = z.infer<typeof quoteResponseSchema>;
